import {
  FormContext,
  FormContextProvider,
  FormSchemaFields,
} from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import AppContext from '../../contexts/AppContext';
import { AppDivisionContext } from '../../contexts/AppDivisionContext';
import useResource from '../../hooks/resource';
import tenantApi from '../../utils/api/tenant';
import FormModal from '../Shared/form/modal/FormModal';

interface ICopyToDivisionModalProps {
  /** Modal title, e.g. "Copy Asset Type to Division". */
  title: string;
  /** Toast shown on a successful copy, e.g. "Asset type copied". */
  successMessage: string;
  /** Copies into the selected divisions (array of `{ id }` objects). */
  onCopy: (divisionIds: Array<{ id: number }>) => Promise<unknown>;
  onClose: () => void;
}

const getSchema = (tenants: any[], divisions: any[], role: string) => {
  const schema: Record<string, any> = {
    tenantId: {
      label: 'Tenant',
      controlType: UIControlType.select,
      options: tenants,
      labelKey: 'name',
      valueKey: 'id',
      validation: { required: true },
      parse: (val: string) => Number.parseInt(val, 10),
    },
    divisionIds: {
      controlType: UIControlType.customTagSelect,
      labelKey: 'name',
      valueKey: 'id',
      isMulti: true,
      openOnFocus: true,
      showClearAll: true,
      label: 'Divisions',
      options: divisions || [],
      noOptionMessage: 'No divisions found',
      placeholder: 'Search for divisions',
      validation: { required: true },
    },
  };

  if (role === 'Admin') {
    delete schema.tenantId;
  }

  return schema;
};

interface ICopyToDivisionUIProps {
  title: string;
  successMessage: string;
  onCopy: (divisionIds: Array<{ id: number }>) => Promise<unknown>;
  tenants: any[];
  role: string;
  onClose: () => void;
}

const CopyToDivisionUI: React.FC<ICopyToDivisionUIProps> = ({
  title,
  successMessage,
  onCopy,
  tenants,
  role,
  onClose,
}) => {
  const formContext = useContext(FormContext);
  const [divisions, setDivisions] = useState<any[]>([]);

  const tenantId = formContext.value.tenantId;

  useEffect(() => {
    if (!tenantId) return;
    tenantApi.divisions
      .index(tenantId)
      .then((data) => {
        setDivisions(data.length ? data : []);
        // Drop any previously-selected divisions that aren't in this tenant.
        const selected = formContext.value.divisionIds || [];

        formContext.set(
          'divisionIds',
          selected.filter((sel: { id: number }) =>
            data.some((d) => d.id === sel.id),
          ),
        );
      })
      .catch(() => toast.error('Failed to load divisions.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const handleSubmit = async (): Promise<void> => {
    try {
      await onCopy(formContext.value.divisionIds);
      toast.success(successMessage);
      onClose();
    } catch {
      toast.error('Failed to copy. Please try again.');
    }
  };

  return (
    <FormModal
      modalProps={{ title, isSmall: false }}
      onCancel={onClose}
      onSubmit={handleSubmit}
      submitText='Copy'
    >
      <div className='asset-copy-form'>
        <FormSchemaFields
          onChange={formContext.set}
          formData={formContext.value}
          schema={getSchema(tenants, divisions, role)}
        />
      </div>
    </FormModal>
  );
};

const CopyToDivisionModal: React.FC<ICopyToDivisionModalProps> = ({
  title,
  successMessage,
  onCopy,
  onClose,
}) => {
  const appStore = useContext(AppContext);
  const appDivisionContext = useContext(AppDivisionContext);
  const user = appStore.get('user');

  const tenantsQuery = useResource({
    resource: 'tenants',
    disablePagination: true,
    divisionId: appDivisionContext.appDivisionId ?? undefined,
  });

  const [initialValue] = useState({
    tenantId: user.tenantId || '',
    divisionIds: [],
  });

  if (tenantsQuery.loading) {
    return null;
  }

  return (
    <FormContextProvider initialValue={initialValue} key='CopyToDivisionForm'>
      <CopyToDivisionUI
        title={title}
        successMessage={successMessage}
        onCopy={onCopy}
        tenants={tenantsQuery.data}
        role={user.roleName}
        onClose={onClose}
      />
    </FormContextProvider>
  );
};

export default CopyToDivisionModal;
